import { useMemo } from 'react';

import { skipToken, useQuery } from '@apollo/client/react';
import {
  Avatar,
  Box,
  IconButton,
  Link,
  Paper,
  Skeleton,
  type SxProps,
  type Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { format, subYears } from 'date-fns';
import { ExternalLink, Pencil, UserIcon } from 'lucide-react';
import {
  Link as RouterLink,
  generatePath,
  useNavigate,
} from 'react-router-dom';

import {
  BANNER_HEIGHT,
  bannerIconButtonSx as iconButtonSx,
} from '@modules/_shared/components/bannerCard';
import { CoverPhoto } from '@modules/_shared/components/CoverPhoto';
import { PublishedChip } from '@modules/_shared/components/PublishedChip';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getAgentPageUrl } from '@modules/_shared/utils/agentUrls';
import { calculateProfileCompletionScore } from '@modules/_shared/utils/profileCompletionScore';
import { ProfileCompletionStatus } from '@modules/visibility-dashboard/components/ProfileBannerCard';
import { useLocale } from '@shared/hooks/locale';

import { GET_DASHBOARD_USER_DATA } from './dashboardQueries';
import { useDashboardModules } from './shared/useDashboardModules';

const AVATAR_SIZE = 100;

const ActionButtons = ({
  agentPageUrl,
  onEdit,
  sx,
}: {
  agentPageUrl: string | null;
  onEdit: () => void;
  sx?: SxProps<Theme>;
}) => {
  const { t } = useLocale();
  return (
    <Box
      sx={[{ display: 'flex', gap: 0.5 }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {agentPageUrl != null && (
        <Tooltip title={t('View public profile')}>
          <IconButton
            size="small"
            component="a"
            href={agentPageUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={iconButtonSx}
          >
            <ExternalLink size={18} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={t('Edit profile')}>
        <IconButton size="small" onClick={onEdit} sx={iconButtonSx}>
          <Pencil size={18} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export const DashboardUserCard = () => {
  const { t, locale } = useLocale();
  const navigate = useNavigate();
  const { me } = useAppData();
  const { isVisibilityEnabled } = useDashboardModules();

  const since = useMemo(
    () => format(subYears(new Date(), 2), 'yyyy-MM-dd'),
    [],
  );

  const { data, dataState, loading } = useQuery(
    GET_DASHBOARD_USER_DATA,
    me?.id ? { variables: { userId: me.id, since } } : skipToken,
  );

  const completeData = dataState === 'complete' ? data : undefined;
  const userData = completeData?.users_by_pk;
  const isSwiss = me?.tenant?.country_code === 'CH';
  const isSmallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  const completionResult = useMemo(
    () =>
      userData
        ? calculateProfileCompletionScore(userData, t, isSwiss, 'agent')
        : null,
    [userData, t, isSwiss],
  );

  const handleEditProfile = () => {
    if (userData?.id) {
      navigate(
        generatePath('/dashboard/users/:userId/edit', { userId: userData.id }),
      );
    }
  };

  const agentPageUrl =
    me?.tenant_id && userData?.show_in_agency_pages
      ? getAgentPageUrl(me.tenant_id, locale, userData.agent_slug)
      : null;

  if (loading && !userData) {
    return (
      <Skeleton
        variant="rounded"
        sx={{ width: '100%', height: BANNER_HEIGHT, borderRadius: '10px' }}
      />
    );
  }

  if (!userData) {
    return null;
  }

  const profileImageUrl = userData.user_images?.[0]?.image?.url;
  // Agents inherit their agency's cover photo for the banner background —
  // the users table has no per-user cover image of its own.
  const coverPhotoUrl = userData.default_team?.cover_photo?.url;
  const fullName =
    userData.full_name ||
    [userData.first_name, userData.last_name].filter(Boolean).join(' ') ||
    t('Unknown');

  const teamId = userData.default_team?.id;
  const teamName = userData.default_team?.name;

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        overflow: 'hidden',
        borderRadius: '10px',
        border: '1px solid',
        borderColor: 'grey.200',
        position: 'relative',
      }}
    >
      <CoverPhoto
        src={coverPhotoUrl}
        width={isSmallScreen ? 700 : 1400}
        sx={{ height: BANNER_HEIGHT, position: 'relative' }}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'flex-end' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 2 },
          px: { xs: 2, sm: 3 },
          pb: 2.5,
          mt: `-${AVATAR_SIZE / 2}px`,
          position: 'relative',
        }}
      >
        <Avatar
          src={profileImageUrl ?? undefined}
          alt={fullName}
          sx={{
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
            border: '3px solid',
            borderColor: 'background.paper',
            boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.12)',
            bgcolor: profileImageUrl ? 'background.paper' : 'grey.200',
            color: 'text.secondary',
            fontSize: '2rem',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {!profileImageUrl &&
            (userData.first_name?.[0] ? (
              `${userData.first_name[0]}${userData.last_name?.[0] ?? ''}`
            ) : (
              <UserIcon size={42} />
            ))}
        </Avatar>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            flex: 1,
            minWidth: 0,
            width: '100%',
            pt: { xs: 0, sm: `${AVATAR_SIZE / 2 + 4}px` },
            pb: 0.5,
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Box sx={{ minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: { xs: 'space-between', sm: 'flex-start' },
                gap: { xs: 1, sm: 0.75 },
                minWidth: 0,
                pt: '10px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  rowGap: 0.25,
                  columnGap: 0.75,
                  minWidth: 0,
                  flex: { xs: 1, sm: 'initial' },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    lineHeight: 1.3,
                    color: 'text.primary',
                    whiteSpace: { xs: 'normal', sm: 'nowrap' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-word',
                  }}
                >
                  {fullName}
                </Typography>
                {isVisibilityEnabled && teamId && teamName && (
                  <>
                    <Typography
                      component="span"
                      sx={{
                        color: 'text.disabled',
                        fontSize: '1.125rem',
                        lineHeight: 1.3,
                        flexShrink: 0,
                        userSelect: 'none',
                        display: { xs: 'none', sm: 'inline' },
                      }}
                    >
                      |
                    </Typography>
                    <Link
                      component={RouterLink}
                      to={generatePath('/visibility/overview/:teamId', {
                        teamId,
                      })}
                      sx={{
                        fontWeight: 500,
                        fontSize: '1.125rem',
                        lineHeight: 1.3,
                        color: 'primary.main',
                        textDecoration: 'none',
                        whiteSpace: { xs: 'normal', sm: 'nowrap' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        wordBreak: 'break-word',
                        minWidth: 0,
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {teamName}
                    </Link>
                  </>
                )}
              </Box>

              <ActionButtons
                agentPageUrl={agentPageUrl}
                onEdit={handleEditProfile}
                sx={{
                  display: { xs: 'flex', sm: 'none' },
                  flexShrink: 0,
                  mt: '-4px',
                }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                mt: 0.75,
              }}
            >
              {isVisibilityEnabled && (
                <PublishedChip
                  isPublished={!!userData.show_in_agency_pages}
                  size="medium"
                />
              )}

              <ProfileCompletionStatus
                completionResult={completionResult}
                profileType="agent"
              />
            </Box>
          </Box>

          <ActionButtons
            agentPageUrl={agentPageUrl}
            onEdit={handleEditProfile}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexShrink: 0,
              alignSelf: 'flex-start',
              // Mirror the agency banner's action column offset so the
              // icons line up with the title text on desktop.
              pt: { xs: 0, sm: '7px' },
            }}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default DashboardUserCard;
